export const enCopyToEMRTranslations = {
  copy_for_emr: {
    modal: {
      summary: 'Summary',
      easyDocumentation: 'Easy documentation',
      easyDocumentationDescription:
        'By using this quick documentation you can save time filling the patient’s EMR.',
      summaryUnavailable: 'Summary unavailable',
      summaryUnavailableDescription:
        'We could not load this activity’s analysis, so the summary would be incomplete.\nPlease try again later rather than copying partial data into the EMR.',
      cancel: 'Cancel',
      back: 'Back',
      copy: 'Copy',
      copied: 'Copied to clipboard!',
    },
    walk: {
      intro:
        'The gait parameters indicate the overall quality of the walk. Walk quality has been directly correlated with health outcomes and fall risk.',
    },
    dual_task: {
      intro:
        'Dual Task Walk — gait under a concurrent cognitive (e.g. serial subtraction) task. Decline in walk score/gait parameters here reflects the attentional demand walking places on the patient and correlates with fall risk and real-world ambulation safety.',
      under_load: {
        above:
          'Under dual-task load, <%= parameterName %> was <%= value %> (above normative range).',
        within:
          'Under dual-task load, <%= parameterName %> was <%= value %> (within normative range).',
        below:
          'Under dual-task load, <%= parameterName %> was <%= value %> (below normative range).',
      },
    },
    walk_score: {
      universal: {
        in_norm_range:
          'Walk score is <%= formattedValue %> and is considered within a typical walking range. The walk score is an aggregated score of gait parameters that indicate the overall quality of a walk.',
        out_norm_range:
          'Walk score is <%= formattedValue %> and is considered atypical. The walk score is an aggregated score of gait parameters that indicate the overall quality of a walk.',
      },
    },
    cadence: {
      universal: {
        in_norm_range:
          'Cadence is <%= formattedValue %> <%= unit %> which is within typical walking range of <%= lowerBound %> <%= unit %> or more, and is one component of an optimal walking pattern.',
        out_norm_range:
          'Cadence is <%= formattedValue %> <%= unit %> which is below the typical range of <%= lowerBound %> <%= unit %> or more. Low cadence can be indicative of impaired gait or decreased endurance.',
      },
      fall_risk: {
        out_norm_range:
          'This value is within the fall risk range of less than <%= lowerBound %> <%= unit %>, and is associated with an increased risk of falling.',
      },
      typical: {
        in_norm_range:
          'Cadence is <%= formattedValue %> <%= unit %>, within the typical <%= lowerBound %> <%= unit %> or higher, one component of an optimal walking pattern.',
        out_norm_range:
          'Cadence is <%= formattedValue %> <%= unit %>, below the typical <%= lowerBound %> <%= unit %> or higher, indicating impaired gait or decreased endurance as well as a higher risk of falls.',
      },
    },
    cadence_variability: {
      universal: {
        in_norm_range:
          'Cadence variability is <%= formattedValue %> <%= unit %> which is within a typical range of <%= threshold %> <%= unit %> or less.',
        out_norm_range:
          'Cadence variability is <%= formattedValue %> <%= unit %> which is worse than the typical range of <%= threshold %> <%= unit %> or less, and could be associated with gait impairment.',
      },
      typical: {
        in_norm_range:
          'Cadence variability is <%= formattedValue %> <%= unit %>, within the typical <%= threshold %> <%= unit %> or lower, indicating a steady step rhythm.',
        out_norm_range:
          'Cadence variability is <%= formattedValue %> <%= unit %>, above the typical <%= threshold %> <%= unit %> or lower, indicating an irregular step rhythm as well as a higher risk of falls.',
      },
    },
    peak_angular_velocity_180: {
      universal: {
        in_norm_range:
          '180° peak turn speed is on average <%= formattedValue %> <%= unit %><% if (lowerBound !== undefined) { %>, within the typical range of <%= lowerBound %> <%= unit %> or more<% } %>.',
        out_norm_range:
          '180° peak turn speed is on average <%= formattedValue %> <%= unit %><% if (lowerBound !== undefined) { %>, below the typical range of <%= lowerBound %> <%= unit %> or more, suggesting reduced dynamic balance<% } %>.',
      },
    },
    peak_angular_velocity_90: {
      universal: {
        in_norm_range:
          '90° peak turn speed is on average <%= formattedValue %> <%= unit %><% if (lowerBound !== undefined) { %>, within the typical range of <%= lowerBound %> <%= unit %> or more<% } %>.',
        out_norm_range:
          '90° peak turn speed is on average <%= formattedValue %> <%= unit %><% if (lowerBound !== undefined) { %>, below the typical range of <%= lowerBound %> <%= unit %> or more, suggesting unsteady direction changes<% } %>.',
      },
    },
    turn_duration_180: {
      universal: {
        in_norm_range:
          '180° turns on average take <%= formattedValue %> <%= unit %><% if (threshold !== undefined) { %>, within the typical range of <%= threshold %> <%= unit %> or less<% } %>.',
        out_norm_range:
          '180° turns on average take <%= formattedValue %> <%= unit %><% if (threshold !== undefined) { %>, longer than the typical range of <%= threshold %> <%= unit %> or less. Taking more than <%= threshold %> seconds to complete a 180° turn has been associated with a higher likelihood of future falls<% } %>.',
      },
    },
    turn_duration_90: {
      universal: {
        in_norm_range:
          '90° turns on average take <%= formattedValue %> <%= unit %><% if (threshold !== undefined) { %>, within the typical range of <%= threshold %> <%= unit %> or less<% } %>.',
        out_norm_range:
          '90° turns on average take <%= formattedValue %> <%= unit %><% if (threshold !== undefined) { %>, longer than the typical range of <%= threshold %> <%= unit %> or less, suggesting hesitant direction changes<% } %>.',
      },
    },
    turning: {
      intro: 'Turn parameters are averaged over walks recorded <%= span %>:',
      span_day: 'on <%= date %>',
      span_range: 'between <%= start %> and <%= end %>',
    },
    velocity: {
      universal: {
        in_norm_range:
          'Velocity is <%= formattedValue %> <%= unit %> which is within the typical range of <%= lowerBound %> <%= unit %> or more, and is associated with better functional mobility.',
        out_norm_range:
          'Velocity is <%= formattedValue %> <%= unit %>, which is below the typical range of <%= lowerBound %> <%= unit %> or more, and is associated with poor cardiorespiratory health, and overall decreased mobility.',
      },
      fall_risk: {
        out_norm_range:
          'This value is within the fall risk range of <%= lowerBound %> <%= unit %> or less, and is associated with an increased risk of falling.',
      },
      typical: {
        in_norm_range:
          'Velocity is <%= formattedValue %> <%= unit %>, within the typical <%= lowerBound %> <%= unit %> or higher, consistent with good functional mobility and a lower risk of falls.',
        out_norm_range:
          'Velocity is <%= formattedValue %> <%= unit %>, below the typical <%= lowerBound %> <%= unit %> or higher, indicating reduced mobility and cardiorespiratory health as well as a higher risk of falls.',
      },
    },
    velocity_variability: {
      universal: {
        in_norm_range:
          'Velocity variability is <%= formattedValue %><%= unit %> which is within the typical range of <%= threshold %><%= unit %> or less.',
        out_norm_range:
          'Velocity variability is <%= formattedValue %><%= unit %> which is worse than the typical range of <%= threshold %><%= unit %> or less, and could be associated with gait impairment.',
      },
    },
    consistency: {
      universal: {
        in_norm_range:
          'Consistency is <%= formattedValue %> which is within the typical range of <%= lowerBound %> - <%= threshold %> and is indicative of consistent stride lengths.',
        out_norm_range:
          'Consistency is <%= formattedValue %> which is less than the typical range of <%= lowerBound %> - <%= threshold %> and is indicative of inconsistent stride lengths and abnormal gait patterns.',
      },
    },
    stride_length: {
      universal: {
        in_norm_range:
          'Stride length is <%= formattedValue %> <%= unit %> which is considered within the typical range of <%= lowerBound %> <%= unit %> or more <% if (gender === "male" || gender === "female") { %> for <%= gender %><% } %>.',
        out_norm_range:
          'Stride length is <%= formattedValue %> <%= unit %> which is considered less than the typical range of <%= lowerBound %> <%= unit %> or more <% if (gender === "male" || gender === "female") { %> for <%= gender %><% } %>, and could be associated with gait impairment.',
      },
    },
    step_length_right: {
      universal: {
        in_norm_range:
          'Step length right is <%= formattedValue %> <%= unit %> which is considered within the typical range of <%= lowerBound %> <%= unit %> or more <% if (gender === "male" || gender === "female") { %> for <%= gender %><% } %>.',
        out_norm_range:
          'Step length right is <%= formattedValue %> <%= unit %> which is considered less than the typical range of <%= lowerBound %> <%= unit %> or more <% if (gender === "male" || gender === "female") { %> for <%= gender %><% } %>, and could be associated with gait impairment.',
      },
    },
    step_length_left: {
      universal: {
        in_norm_range:
          'Step length left is <%= formattedValue %> <%= unit %> which is considered within the typical range of <%= lowerBound %> <%= unit %> or more <% if (gender === "male" || gender === "female") { %> for <%= gender %><% } %>.',
        out_norm_range:
          'Step length left is <%= formattedValue %> <%= unit %> which is considered less than the typical range of <%= lowerBound %> <%= unit %> or more <% if (gender === "male" || gender === "female") { %> for <%= gender %><% } %>, and could be associated with gait impairment.',
      },
    },
    step_length_asymmetry: {
      universal: {
        in_norm_range:
          'Step length asymmetry is <%= formattedValue %><%= unit %> and is within the typical range of <%= threshold %><%= unit %> or less.',
        out_norm_range:
          'Step length asymmetry is <%= formattedValue %><%= unit %> which is worse than the typical range of <%= threshold %><%= unit %> or less, indicating unequal right/left step length.',
      },
      fall_risk: {
        out_norm_range:
          'This value is within the fall risk range of <%= threshold %><%= unit %> or more, and is associated with an increased risk of falling.',
      },
      typical: {
        in_norm_range:
          'Step length asymmetry is <%= formattedValue %><%= unit %>, within the typical <%= threshold %><%= unit %> or lower, indicating balanced right/left step length.',
        out_norm_range:
          'Step length asymmetry is <%= formattedValue %><%= unit %>, above the typical <%= threshold %><%= unit %> or lower, indicating unequal right/left step length as well as a higher risk of falls.',
      },
    },
    base_width: {
      universal: {
        in_norm_range:
          'Base width is <%= formattedValue %> <%= unit %> and is within the typical range of <%= lowerBound %> <%= unit %> - <%= threshold %> <%= unit %>.',
        out_norm_range:
          'Base width is <%= formattedValue %> <%= unit %> and is outside a typical range between <%= lowerBound %> <%= unit %> - <%= threshold %> <%= unit %>. High base width is correlated with impaired balance. Low base width is correlated with a scissoring style of gait.',
      },
    },
    double_support: {
      universal: {
        in_norm_range:
          'Double support is <%= formattedValue %><%= unit %> which is considered within the typical range of <%= threshold %><%= unit %> or less.',
        out_norm_range:
          'The double support is <%= formattedValue %><%= unit %> and is worse than the typical range of <%= threshold %><%= unit %> or less. High double support is associated with a slow gait and decreased swing-to-stance time ratio.',
      },
      fall_risk: {
        out_norm_range:
          'This value is within the fall risk range of <%= threshold %><%= unit %> or more, and is associated with an increased risk of falling.',
      },
      typical: {
        in_norm_range:
          'Double support is <%= formattedValue %><%= unit %>, within the typical <%= threshold %><%= unit %> or lower, consistent with steady weight transfer.',
        out_norm_range:
          'Double support is <%= formattedValue %><%= unit %>, above the typical <%= threshold %><%= unit %> or lower, indicating a slower, more cautious gait as well as a higher risk of falls.',
      },
    },
    double_support_asymmetry: {
      universal: {
        in_norm_range:
          'Double support asymmetry is <%= formattedValue %><%= unit %> and is within the typical range of <%= threshold %><%= unit %> or less.',
        out_norm_range:
          'Double support asymmetry is <%= formattedValue %><%= unit %> and is worse than the typical range of <%= threshold %><%= unit %> or less, indicating impaired gait patterns.',
      },
    },
    single_support_left: {
      universal: {
        in_norm_range:
          'Single support left is <%= formattedValue %><%= unit %> which is considered within the typical range of <%= lowerBound %><%= unit %> or more.',
        out_norm_range:
          'Single support left is <%= formattedValue %><%= unit %> which is worse than the typical range of <%= lowerBound %><%= unit %> or more, and is correlated with slow gait and decreased swing time on the contralateral leg.',
      },
    },
    single_support_right: {
      universal: {
        in_norm_range:
          'Single support right is <%= formattedValue %><%= unit %> which is considered within the typical range of <%= lowerBound %><%= unit %> or more.',
        out_norm_range:
          'Single support right is <%= formattedValue %><%= unit %> which is worse than the typical range of <%= lowerBound %><%= unit %> or more,  and is correlated with slow gait and decreased swing time on the contralateral leg.',
      },
    },
    stance_left: {
      universal: {
        in_norm_range:
          'Left stance is <%= formattedValue %><%= unit %> which is considered within the typical range of <%= threshold %><%= unit %> or less.',
        out_norm_range:
          'Left stance is <%= formattedValue %><%= unit %> which is worse than the typical range of <%= threshold %><%= unit %> or less, and is correlated with slow gait and decreased swing time on the contralateral leg.',
      },
    },
    stance_right: {
      universal: {
        in_norm_range:
          'Right stance is <%= formattedValue %><%= unit %> which is considered within the typical range of <%= threshold %><%= unit %> or less.',
        out_norm_range:
          'Right stance is <%= formattedValue %><%= unit %> which is worse than the typical range of <%= threshold %><%= unit %> or less, and is correlated with slow gait and decreased swing time on the contralateral leg.',
      },
    },
    stance_asymmetry: {
      universal: {
        in_norm_range:
          'Stance asymmetry is <%= formattedValue %><%= unit %> and is within the typical range of <%= threshold %><%= unit %> or less.',
        out_norm_range:
          'Stance asymmetry is <%= formattedValue %><%= unit %> which is worse than the typical range of <%= threshold %><%= unit %> or less, indicating unequal right/left stance time.',
      },
      fall_risk: {
        out_norm_range:
          'This value is within the fall risk range of <%= threshold %><%= unit %> or more, and is associated with an increased risk of falling.',
      },
      typical: {
        in_norm_range:
          'Stance asymmetry is <%= formattedValue %><%= unit %>, within the typical <%= threshold %><%= unit %> or lower, indicating balanced right/left stance time.',
        out_norm_range:
          'Stance asymmetry is <%= formattedValue %><%= unit %>, above the typical <%= threshold %><%= unit %> or lower, indicating unequal right/left stance time as well as a higher risk of falls.',
      },
    },
    step_length_diff: {
      universal: {
        in_norm_range:
          'Step length difference is <%= formattedValue %><%= unit %> which is considered within the typical range of <%= threshold %><%= unit %> or less.',
        out_norm_range:
          'Step length difference is <%= formattedValue %><%= unit %> which is worse than the typical range of <%= threshold %><%= unit %> or less, indicating abnormal step length discrepancy.',
      },
    },
    hip_range: {
      universal: {
        in_norm_range:
          'Total hip ROM during the gait cycle is <%= formattedValue %> <%= unit %>, which is within the typical range of <%= lowerBound %> <%= unit %> - <%= threshold %> <%= unit %>. ',
        out_norm_range:
          'Total hip ROM during the gait cycle is <%= formattedValue %> <%= unit %>, and is less than the typical range of <%= lowerBound %> <%= unit %> - <%= threshold %> <%= unit %>.',
      },
    },
    functional_reach_distance_cm: {
      universal: {
        in_norm_range:
          'Functional Reach test score is <%= formattedValue %> <%= unit %> which is considered within the typical range of <%= lowerBound %> <%= unit %> or more<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>.',
        out_norm_range:
          'Functional Reach test score is <%= formattedValue %> <%= unit %> which is considered worse than the typical range of <%= lowerBound %> <%= unit %> or more<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, and is associated with decreased balance and an increased risk of falling.',
      },
    },
    fear_of_falls_score: {
      universal: {
        in_norm_range:
          'The fall confidence score is <%= formattedValue %> <% if (lowerBound && threshold) { %> and is within the typical range of <%= lowerBound %> - <%= threshold %> for this test<% } %>.',
        out_norm_range:
          'The fall confidence score is <%= formattedValue %> <% if (lowerBound && threshold) { %> and is worse than the typical range of <%= lowerBound %> - <%= threshold %>, indicating a fear of falls<% } %> .',
      },
      fall_risk: {
        out_norm_range:
          'This value is within the fall risk range of <%= threshold %> or more, and is associated with an increased risk of falling.',
      },
      typical: {
        in_norm_range:
          'The fall confidence score is <%= formattedValue %><% if (lowerBound && threshold) { %>, within the typical <%= lowerBound %> to <%= threshold %> for this test<% } %>, indicating good confidence in balance.',
        out_norm_range:
          'The fall confidence score is <%= formattedValue %><% if (lowerBound && threshold) { %>, above the typical <%= lowerBound %> to <%= threshold %><% } %>, indicating a fear of falling as well as a higher risk of falls.',
      },
    },
    tug_duration_seconds: {
      universal: {
        in_norm_range:
          'Based on measurements done using OneStep on <%= date %> , <%= name %> completed a standard TUG test. The TUG score is <%= formattedValue %> <%= unit %>, which is within the typical range of <%= threshold %> <%= unit %> or less<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>.',
        out_norm_range:
          'Based on measurements done using OneStep on <%= date %> , <%= name %> completed a standard TUG test. The TUG score is <%= formattedValue %> <%= unit %>, which is worse than the typical range of <%= threshold %> <%= unit %> or less<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>.',
      },
      fall_risk: {
        out_norm_range:
          'This value is considered to be within the fall risk range of <%= threshold %> <%= unit %> or more<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, and is associated with an increased risk of falling.',
      },
      typical: {
        in_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a standard TUG test. The TUG score is <%= formattedValue %> <%= unit %>, within the typical <%= threshold %> <%= unit %> or lower<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, indicating adequate balance and functional mobility.',
        out_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a standard TUG test. The TUG score is <%= formattedValue %> <%= unit %>, above the typical <%= threshold %> <%= unit %> or lower<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, indicating declining balance and functional mobility as well as a higher risk of falls.',
      },
    },
    sts_repetition_count: {
      universal: {
        in_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a 30 sec STS test. The STS score is <%= formattedValue %> <%= unit %>, which is within the typical range of <%= lowerBound %> <%= unit %> or more<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>. ',
        out_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a 30 sec STS test. The STS score is <%= formattedValue %> <%= unit %>, and is worse than the typical range of <%= lowerBound %> <%= unit %> or more<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>. This result suggests reduced functional strength in the lower extremities.',
      },
      fall_risk: {
        out_norm_range:
          'This value is within the fall risk range of <%= lowerBound %> <%= unit %> or less<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, and is associated with an increased risk of falling.',
      },
      typical: {
        in_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a 30 sec STS test. The STS score is <%= formattedValue %> <%= unit %>, within the typical <%= lowerBound %> <%= unit %> or higher<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, indicating adequate lower-extremity strength.',
        out_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a 30 sec STS test. The STS score is <%= formattedValue %> <%= unit %>, below the typical <%= lowerBound %> <%= unit %> or higher<% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, indicating reduced lower-extremity strength as well as a higher risk of falls.',
      },
    },
    distance: {
      universal: {
        in_norm_range:
          'Distance walked is <%= formattedValue %> <%= unit %> which indicates the patient can ambulate household distances.',
        out_norm_range:
          'Distance walked is <%= formattedValue %> <%= unit %> which indicates the patient cannot ambulate household distances safely.',
      },
    },
    endurance_test: {
      section_title: 'Distance',
      distance:
        'The patient ambulated <%= distance %><%= normReference %> during the <%= abbreviation %><% if (stopsLabel) { %> with <%= stopsLabel %><% } %><% if (interpretation) { %>, which is consistent with <%= interpretation %><% } %>.',
      abbreviation: {
        '2mw': '2MWT',
        '6mw': '6MWT',
      },
      stops: {
        none: '0 stops',
        summary:
          '<%= count %> <% if (count === 1) { %>stop<% } else { %>stops<% } %> (cumulative duration <%= duration %> <%= durationUnit %>)',
      },
      norm_reference: ' (~<%= percent %>% of the <%= distance %> reference distance)',
      capacity: {
        high_level_ambulator:
          'high-level community ambulation, sustaining speed and endurance across demanding environments',
        full_community_ambulator:
          'full community ambulation across typical daily distances and public environments',
        limited_community_ambulator:
          'limited community ambulation, with difficulty sustaining longer distances',
        household_ambulator:
          'being below the commonly accepted thresholds for functional community ambulation, reflecting household-level ambulation and decreased walking endurance',
        unknown: 'an ambulation capacity that could not be determined',
      },
    },
    plus_m_score: {
      universal: {
        in_norm_range:
          'The PLUS-M T score for <%= name %> on <%= date %> is <%= formattedValue %> which is above the mean of 50. A higher T score indicates a higher level of mobility.',
        out_norm_range:
          'The PLUS-M T score for <%= name %> on <%= date %> is <%= formattedValue %> which is below the mean of 50. A lower T score indicates a lower level of mobility.',
      },
    },
    lefs_score: {
      universal: {
        in_norm_range:
          'The LEFS score for <%= name %> on <%= date %> is <%= formattedValue %> out of a possible 80 points. A higher score indicates better functional mobility.',
        out_norm_range:
          'The LEFS score for <%= name %> on <%= date %> is <%= formattedValue %> out of a possible 80 points. A lower score indicates decreased function.',
      },
    },
    psfs_score: {
      universal: {
        in_norm_range:
          'The PSFS score for <%= name %> on <%= date %> is <%= formattedValue %>. A higher score indicates better functional mobility.',
        out_norm_range:
          'The PSFS score for <%= name %> on <%= date %> is <%= formattedValue %>. A lower score indicates lower perceived functional mobility.',
      },
    },
    vas_score: {
      universal: {
        in_norm_range:
          'The VAS score for <%= name %> on <%= date %> is <%= formattedValue %>. A lower score indicates lower pain levels.',
        out_norm_range:
          'The VAS score for <%= name %> on <%= date %> is <%= formattedValue %>. A higher score indicates higher pain levels.',
      },
    },
    amppro_score: {
      universal: {
        in_norm_range:
          'The AMPPRO score for <%= name %> on <%= date %> is <%= formattedValue %>. This score is associated with K level <%= kValue %>.',
        out_norm_range:
          'The AMPPRO score for <%= name %> on <%= date %> is <%= formattedValue %>. This score is associated with K level <%= kValue %>.',
      },
    },
    berg_score: {
      universal: {
        in_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a standard Berg Balance Scale (BBS) test. The BBS score is <%= formattedValue %><% if (maxScore) { %>/<%= maxScore %><% } %><% if (lowerBound) { %>, which is within the typical range of <%= typicalBound %><% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, and is associated with adequate balance and a low risk of falling<% } %>.',
        out_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a standard Berg Balance Scale (BBS) test. The BBS score is <%= formattedValue %><% if (maxScore) { %>/<%= maxScore %><% } %><% if (lowerBound) { %>, which is below the typical range of <%= typicalBound %><% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, and is associated with impaired balance and an increased risk of falling<% } %>.',
      },
    },
    tinetti_score: {
      universal: {
        in_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a standard Tinetti (POMA) test. The Tinetti score is <%= formattedValue %><% if (maxScore) { %>/<%= maxScore %><% } %><% if (lowerBound) { %>, which is within the typical range of <%= typicalBound %><% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, and is associated with adequate balance and a low risk of falling<% } %>.',
        out_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a standard Tinetti (POMA) test. The Tinetti score is <%= formattedValue %><% if (maxScore) { %>/<%= maxScore %><% } %><% if (lowerBound) { %>, which is below the typical range of <%= typicalBound %><% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, and is associated with impaired balance and an increased risk of falling<% } %>.',
      },
    },
    fullerton_balance_score: {
      universal: {
        in_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a standard Fullerton Advanced Balance (FAB) scale. The FAB score is <%= formattedValue %><% if (maxScore) { %>/<%= maxScore %><% } %><% if (lowerBound) { %>, which is within the typical range of <%= typicalBound %><% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, and is associated with adequate balance and a low risk of falling<% } %>.',
        out_norm_range:
          'Based on measurements done using OneStep on <%= date %>, <%= name %> completed a standard Fullerton Advanced Balance (FAB) scale. The FAB score is <%= formattedValue %><% if (maxScore) { %>/<%= maxScore %><% } %><% if (lowerBound) { %>, which is below the typical range of <%= typicalBound %><% if (age) { %> for<% if (gender === "male" || gender === "female") { %> <%= gender %><% } %> <%= age %> years old<% } %>, and is associated with impaired balance and an increased risk of falling<% } %>.',
      },
    },
  },
};
